/**
 * PATH: packages/job-hub/src/jobs/qa/qaPlanApi.ts
 * FILE_ID: JOBHUB-QA-PLAN-API-001
 * PURPOSE: Quote-source preview and QA generation endpoint client.
 */
import type { JobHubHttpClient, JobHubRequestConfig } from '../jobHubDataAdapter'
import { classificationPayload } from './classification'
import { toQuoteSheetOptions } from './qaApiHelpers'
import type { QaApi, QaInspectionSummary, QaPlanPreview } from './qaTypes'

type PlanApi = Pick<
  QaApi,
  | 'listQuoteSheets'
  | 'previewQaPlan'
  | 'generateFromQuoteSheet'
  | 'generateFromQuoteSheets'
  | 'retryQuoteIngest'
>

export function createQaPlanApi(
  client: JobHubHttpClient,
  headers: Record<string, string> | undefined,
): PlanApi {
  return {
    async listQuoteSheets(jobId) {
      const response = await client.get(`/api/v1/jobs/${jobId}/quote-sheets/`, { headers })
      return toQuoteSheetOptions(response.data)
    },
    async retryQuoteIngest(quoteSheetId) {
      // The existing AI reprocess door, not a new endpoint: it already knows
      // how to re-read a stored file, and the backend extended it to
      // re-enqueue the ingest task for a failure that never stored one.
      await client.post(
        `/api/v1/quote-sheets/${quoteSheetId}/ai/reprocess/`,
        {},
        { headers },
      )
    },
    async previewQaPlan(quoteSheetIds, mode, explicitlyIncludedSourceIds) {
      const response = await client.get('/api/v1/quote-sheets/qa-plan-preview/', {
        params: {
          ids: quoteSheetIds.join(','),
          mode,
          ...(explicitlyIncludedSourceIds?.length
            ? { include_ids: explicitlyIncludedSourceIds.join(',') }
            : {}),
        },
        headers,
        timeout: 120000,
      } as JobHubRequestConfig)
      return response.data as QaPlanPreview
    },
    async generateFromQuoteSheet(quoteSheetId, classification, provenance) {
      const response = await client.post(
        '/api/qa/inspections/from-quote-sheet/',
        {
          quote_sheet_id: quoteSheetId,
          ...(classification ? classificationPayload(classification) : {}),
          ...(provenance ?? {}),
        },
        { headers, timeout: 120000 } as JobHubRequestConfig,
      )
      return response.data as QaInspectionSummary
    },
    async generateFromQuoteSheets(quoteSheetIds, classification, provenance) {
      const response = await client.post(
        '/api/qa/inspections/from-quote-sheet/',
        {
          quote_sheet_ids: quoteSheetIds,
          ...(classification ? classificationPayload(classification) : {}),
          ...(provenance ?? {}),
        },
        { headers, timeout: 120000 } as JobHubRequestConfig,
      )
      return response.data as QaInspectionSummary
    },
  }
}
